import React, { Dispatch, SetStateAction, useContext, useMemo, useRef, useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { Box, Button, Flex } from '@chakra-ui/react';
import { FieldArray, FieldArrayRenderProps, Form, Formik } from 'formik';
import { toast } from 'react-toastify';

import { DealStateEnum } from '../../../../gql/dealGql';
import {
  DealMediaTypeEnum,
  GetUploadUrlDocument,
  Maybe,
  useMediaInsertMutation,
} from '../../../../gql/generated/graphql';

import { MediaModalType } from '../MediaModal';

import MediaUploadForm from './MediaUploadForm';

import { DealMediaTypeDateFieldMap } from '../../../../constants/media';
import useLazyExtractDataFromFile from '../../../../hooks/useLazyExtractDataFromFile';
import useUpdateCustomDates from '../../../../hooks/useUpdateCustomDates';
import { DealContext } from '../../../../libs/DealContext';
import { ModalContext } from '../../../../libs/contextLib';
import { DealMediaEdit, GetUploadUrlResult } from '../../../../types/media';
import { ACCEPT_MIME_STRING } from '../../../../utils/fileValidation';
import { getFileNameAndExtension, normalizeFileName } from '../../../../utils/files';
import { transformMetadata } from '../../../../utils/media';
import Dropzone from '../../../Dropzone';
import { getValidationSchema } from '../../utils';

interface MediaUploadProps {
  cancelFn: () => void;
  mediaRefetch: () => void;
  showModal: MediaModalType;
  setShowModal: Dispatch<SetStateAction<MediaModalType>>;
  initialMediaType?: Maybe<DealMediaTypeEnum>;
}

const MediaUpload: React.FC<MediaUploadProps> = ({
  cancelFn,
  mediaRefetch,
  showModal,
  setShowModal,
  initialMediaType,
}) => {
  const { deal } = useContext(DealContext);
  const { setModal } = useContext(ModalContext);

  const client = useApolloClient();
  const [mediaInsert] = useMediaInsertMutation();
  const { extractDataFromFile } = useLazyExtractDataFromFile();
  const updateCustomDates = useUpdateCustomDates();

  const [uploading, setUploading] = useState(false);
  const arrayHelpersRef = useRef<FieldArrayRenderProps>();

  const initialValues: {
    dealMedia: DealMediaEdit[];
  } = useMemo(
    () => ({
      dealMedia: [],
    }),
    [],
  );

  const handleDrop = async (acceptedFiles: File[]) => {
    if (!acceptedFiles.length) {
      return;
    }

    const newMedia = await Promise.all(
      acceptedFiles.map(async (file): Promise<DealMediaEdit> => {
        const normalizedFileName = normalizeFileName(file.name);
        const { fileName, extension } = getFileNameAndExtension(normalizedFileName);

        return {
          selectedFile: file,
          sourceFileName: normalizedFileName,
          fileName,
          extension,
          type: null,
          [DealStateEnum.TitleReceived]:
            deal.deal_dates?.custom_dates?.[DealStateEnum.TitleReceived],
          wet_sig_received_date: deal.deal_dates?.custom_dates?.wet_sig_received_date,
        };
      }),
    );

    newMedia.forEach((file) => {
      arrayHelpersRef.current?.push(file);
    });
  };

  const onSubmit = async (values: { dealMedia: DealMediaEdit[] }) => {
    if (!values.dealMedia.length) {
      return;
    }

    setUploading(true);
    try {
      const dealId = deal.id;
      if (!dealId) {
        throw new Error();
      }

      await Promise.all(
        values.dealMedia.map(async (file) => {
          const { data } = await client.query<GetUploadUrlResult>({
            query: GetUploadUrlDocument,
            variables: {
              dealId,
              fileName: normalizeFileName(`${file.fileName}.${file.extension}`),
            },
          });

          const { url, key } = data.getUploadUrl;
          await fetch(url, {
            method: 'PUT',
            body: file.selectedFile,
          });

          const extractedMetadata = await extractDataFromFile(
            file.type,
            file.metadata,
            file.selectedFile,
          );

          if (file.type && DealMediaTypeDateFieldMap[file.type]) {
            const mediaTypeDateField = DealMediaTypeDateFieldMap[file.type];
            const { field: dateField } = mediaTypeDateField ?? {};

            if (dateField && file[dateField]) {
              // Force initial values recalculation.
              setModal({
                MediaModal: true,
              });
              await updateCustomDates({
                fieldName: dateField,
                fieldValue: file[dateField],
                componentName: 'MediaUpload.tsx',
              });
              setModal({
                MediaModal: false,
              });
            }
          }

          return mediaInsert({
            variables: {
              key,
              dealId,
              type: file.type,
              metadata: transformMetadata(extractedMetadata),
            },
          });
        }),
      );

      mediaRefetch();
      toast.success('Files uploaded!');
    } catch {
      toast.error('Unable to upload files');
    }

    setUploading(false);
    cancelFn();
  };

  return (
    <Box>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={getValidationSchema(deal.deal_dates?.custom_dates)}
        onSubmit={onSubmit}
      >
        {({ values, handleSubmit }) => (
          <Form onSubmit={handleSubmit}>
            <Flex direction="column" justifyContent="center">
              <FieldArray
                name="dealMedia"
                render={(arrayHelpers) => {
                  arrayHelpersRef.current = arrayHelpers;
                  const { remove } = arrayHelpers;

                  if (values.dealMedia.length === 0) {
                    return null;
                  }

                  return (
                    <Flex direction="column" ml={3} flex={1}>
                      {values.dealMedia.map((media, index) => (
                        <MediaUploadForm
                          // eslint-disable-next-line react/no-array-index-key
                          key={index}
                          showModal={showModal}
                          setShowModal={setShowModal}
                          index={index}
                          remove={remove}
                          initialMediaType={initialMediaType}
                        />
                      ))}
                    </Flex>
                  );
                }}
              />
              <Box maxW="350px" margin="auto">
                <Dropzone
                  onDrop={handleDrop}
                  multiple
                  accept={ACCEPT_MIME_STRING}
                  showFiles={false}
                />
              </Box>
            </Flex>
            <Flex direction="row" justifyContent="end" mt={5}>
              {values.dealMedia.length > 0 && (
                <Button type="submit" isLoading={uploading} loadingText="UPLOAD">
                  UPLOAD
                </Button>
              )}
            </Flex>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default MediaUpload;
